import {
  CalendarDays,
  Check,
  Clock,
  Edit3,
  FileText,
  Filter,
  GraduationCap,
  Layers3,
  Palette,
  Plus,
  RotateCcw,
  Save,
  Search,
  Settings2,
  Trash2,
  Upload,
  X
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  colorOptions,
  defaultDepartments,
  loadDepartments,
  persistDepartments,
  slugify,
  splitCodes,
  tintColor
} from "./departments";
import { parseIcalFile, rematchDepartments } from "./ical";
import type { Department, DepartmentDraft, LessonEvent, SortMode, UploadSummary } from "./types";
import type { AnalyticsRange, AnalyticsScope } from "./types";

type FilterId = "all" | "unknown" | string;
type ViewMode = "list" | "compact";

const emptyDraft: DepartmentDraft = {
  name: "",
  codes: "",
  color: "#64748b",
  parentId: ""
};

export function App() {
  const [departments, setDepartments] = useState<Department[]>(loadDepartments);
  const [events, setEvents] = useState<LessonEvent[]>([]);
  const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<FilterId>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortMode>("date");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [groupSubDepartments, setGroupSubDepartments] = useState(true);
  const [analyticsRange, setAnalyticsRange] = useState<AnalyticsRange>("12m");
  const [analyticsScope, setAnalyticsScope] = useState<AnalyticsScope>("past");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [dragging, setDragging] = useState(false);
  const [departmentDraft, setDepartmentDraft] = useState<DepartmentDraft | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const today = useMemo(() => startOfToday(), []);
  const visibleDepartments = useMemo(() => (groupSubDepartments ? departments.filter((department) => !department.parentId) : departments), [departments, groupSubDepartments]);
  const upcomingEvents = useMemo(() => events.filter((event) => event.start >= today), [events, today]);
  const pastEvents = useMemo(() => events.filter((event) => event.start < today), [events, today]);
  const analyticsWindow = useMemo(() => getAnalyticsWindow(events, today, analyticsRange, analyticsScope, customStart, customEnd), [events, today, analyticsRange, analyticsScope, customStart, customEnd]);
  const analyticsEvents = useMemo(() => events.filter((event) => event.start >= analyticsWindow.start && event.start <= analyticsWindow.end), [events, analyticsWindow]);

  const unknownCodes = useMemo(() => {
    const counts = new Map<string, number>();
    upcomingEvents.forEach((event) => {
      const code = event.moduleCode || event.code;
      if (!event.department && code) counts.set(code, (counts.get(code) || 0) + 1);
    });
    return [...counts.entries()].map(([code, count]) => ({ code, count })).sort((a, b) => a.code.localeCompare(b.code));
  }, [upcomingEvents]);

  const visibleEvents = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return upcomingEvents
      .filter((event) => {
        const matchesDepartment =
          selectedDepartmentId === "all" ||
          (selectedDepartmentId === "unknown" && !event.department) ||
          getDisplayDepartment(event, departments, groupSubDepartments)?.id === selectedDepartmentId ||
          (!groupSubDepartments && event.department?.parentId === selectedDepartmentId);

        const searchText = [
          event.topic,
          event.moduleCode,
          event.code,
          event.delivery,
          event.department?.name,
          getDisplayDepartment(event, departments, groupSubDepartments)?.name,
          event.summary
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return matchesDepartment && (!normalizedSearch || searchText.includes(normalizedSearch));
      })
      .sort((a, b) => {
        if (sort === "department") {
          return (getDisplayDepartment(a, departments, groupSubDepartments)?.name || "Unrecognized").localeCompare(getDisplayDepartment(b, departments, groupSubDepartments)?.name || "Unrecognized") || a.start.getTime() - b.start.getTime();
        }
        if (sort === "topic") {
          return (a.topic || a.summary).localeCompare(b.topic || b.summary) || a.start.getTime() - b.start.getTime();
        }
        return a.start.getTime() - b.start.getTime();
      });
  }, [upcomingEvents, search, selectedDepartmentId, sort, departments, groupSubDepartments]);

  const eventsByDay = useMemo(() => groupByDay(visibleEvents), [visibleEvents]);
  const activeDepartmentCount = new Set(upcomingEvents.map((event) => getDisplayDepartment(event, departments, groupSubDepartments)?.id).filter(Boolean)).size;
  const nextLesson = upcomingEvents[0];
  const departmentInsights = useMemo(() => buildDepartmentInsights(analyticsEvents, departments, groupSubDepartments), [analyticsEvents, departments, groupSubDepartments]);
  const monthlyInsights = useMemo(() => buildMonthlyInsights(analyticsEvents, analyticsWindow.start, analyticsWindow.end), [analyticsEvents, analyticsWindow]);
  const topicInsights = useMemo(() => buildTopicInsights(analyticsEvents), [analyticsEvents]);
  const deliveryInsights = useMemo(() => buildDeliveryInsights(analyticsEvents), [analyticsEvents]);

  async function importFile(file: File) {
    setError("");
    try {
      const text = await file.text();
      const result = parseIcalFile(text, departments, file.name);
      setEvents(result.events);
      setUploadSummary(result.summary);
      setSelectedDepartmentId("all");
      setExpandedEventId(null);
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : "The iCal file could not be read.");
    }
  }

  function updateDepartments(nextDepartments: Department[]) {
    setDepartments(nextDepartments);
    persistDepartments(nextDepartments);
    setEvents((currentEvents) => rematchDepartments(currentEvents, nextDepartments));
  }

  function saveDepartment() {
    if (!departmentDraft) return;
    const name = departmentDraft.name.trim();
    const codes = splitCodes(departmentDraft.codes);
    if (!name || !codes.length) return;

    const id = departmentDraft.id || slugify(`${name}-${codes.join("-")}`);
    const nextDepartment: Department = {
      id,
      name,
      codes,
      color: departmentDraft.color || "#64748b",
      parentId: departmentDraft.parentId || undefined
    };

    const exists = departments.some((department) => department.id === id);
    const nextDepartments = exists
      ? departments.map((department) => (department.id === id ? { ...department, ...nextDepartment, builtin: department.builtin } : department))
      : [...departments, nextDepartment];

    updateDepartments(nextDepartments);
    setDepartmentDraft(null);
  }

  function deleteDepartment(id: string) {
    const nextDepartments = departments.filter((department) => department.id !== id);
    updateDepartments(nextDepartments);
    if (selectedDepartmentId === id) setSelectedDepartmentId("all");
    setDepartmentDraft(null);
  }

  function resetDepartments() {
    updateDepartments(defaultDepartments);
    setSelectedDepartmentId("all");
  }

  return (
    <main className="app-shell">
      <header className="app-header">
        <div>
          <p className="eyebrow">iCal lesson planner</p>
          <h1>Upcoming lessons</h1>
        </div>
        <div className="header-actions">
          <button className="secondary-button" type="button" onClick={() => setViewMode(viewMode === "list" ? "compact" : "list")}>
            <Layers3 size={18} />
            {viewMode === "list" ? "Compact" : "Detailed"}
          </button>
          <button className="primary-button" type="button" onClick={() => fileInputRef.current?.click()}>
            <Upload size={18} />
            Upload iCal
          </button>
          <input ref={fileInputRef} className="sr-only" type="file" accept=".ics,text/calendar" onChange={(event) => event.target.files?.[0] && importFile(event.target.files[0])} />
        </div>
      </header>

      <section className="layout">
        <aside className="sidebar">
          <section
            className={`drop-panel ${dragging ? "dragging" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const file = event.dataTransfer.files[0];
              if (file) void importFile(file);
            }}
          >
            <div className="drop-icon">
              <FileText size={26} />
            </div>
            <h2>Upload a lesson schedule</h2>
            <p>Drop an `.ics` file here to import upcoming lessons and detect department codes.</p>
            <button className="link-button" type="button" onClick={() => fileInputRef.current?.click()}>
              Choose file
            </button>
            {uploadSummary && <p className="file-note">{uploadSummary.fileName}</p>}
            {error && <p className="error-note">{error}</p>}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>Filters</h2>
              <Filter size={18} />
            </div>
            <div className="filter-list">
              <DepartmentFilter
                active={selectedDepartmentId === "all"}
                color="#202124"
                count={upcomingEvents.length}
                label="All departments"
                onClick={() => setSelectedDepartmentId("all")}
              />
              <DepartmentFilter
                active={selectedDepartmentId === "unknown"}
                color="#8b8d94"
                count={upcomingEvents.filter((event) => !event.department).length}
                label="Unrecognized"
                onClick={() => setSelectedDepartmentId("unknown")}
              />
              {departments.map((department) => (
                visibleDepartments.includes(department) && (
                <DepartmentFilter
                  key={department.id}
                  active={selectedDepartmentId === department.id}
                  color={department.color}
                  count={upcomingEvents.filter((event) => getDisplayDepartment(event, departments, groupSubDepartments)?.id === department.id || (!groupSubDepartments && event.department?.id === department.id)).length}
                  label={department.name}
                  detail={department.parentId ? `${getDepartmentById(departments, department.parentId)?.name || "Sub-department"} · ${department.codes.join(", ")}` : department.codes.join(", ")}
                  onClick={() => setSelectedDepartmentId(department.id)}
                  onEdit={() => setDepartmentDraft({ id: department.id, name: department.name, codes: department.codes.join(", "), color: department.color, parentId: department.parentId || "" })}
                />
                )
              ))}
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>Unrecognized codes</h2>
              <span className="count-pill">{unknownCodes.length}</span>
            </div>
            {unknownCodes.length ? (
              <div className="unknown-list">
                {unknownCodes.map((item) => (
                  <button
                    key={item.code}
                    className="unknown-row"
                    type="button"
                    onClick={() => setDepartmentDraft({ ...emptyDraft, codes: item.code })}
                  >
                    <span className="swatch neutral" />
                    <span>
                      <strong>{item.code}</strong>
                      <small>
                        {item.count} upcoming lesson{item.count === 1 ? "" : "s"}
                      </small>
                    </span>
                    <Plus size={18} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="muted">Every detected code has a department.</p>
            )}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>Departments</h2>
              <Settings2 size={18} />
            </div>
            <div className="department-actions">
              <button className="secondary-button full" type="button" onClick={() => setDepartmentDraft(emptyDraft)}>
                <Plus size={18} />
                Add department
              </button>
              <button className="ghost-button full" type="button" onClick={resetDepartments}>
                <RotateCcw size={18} />
                Reset defaults
              </button>
              <button className={`ghost-button full ${groupSubDepartments ? "active-mode" : ""}`} type="button" onClick={() => setGroupSubDepartments((value) => !value)}>
                <Layers3 size={18} />
                {groupSubDepartments ? "Grouped view" : "Separate view"}
              </button>
            </div>
          </section>
        </aside>

        <section className="content">
          <section className="stats-grid">
            <StatCard label="Upcoming lessons" value={upcomingEvents.length} icon={<CalendarDays size={22} />} />
            <StatCard label="Active departments" value={activeDepartmentCount} icon={<GraduationCap size={22} />} />
            <StatCard label="Needs mapping" value={unknownCodes.length} icon={<Palette size={22} />} />
            <StatCard label="Past lessons" value={pastEvents.length} icon={<Check size={22} />} />
          </section>

          <section className="control-strip">
            <label className="search-field">
              <Search size={18} />
              <input value={search} type="search" placeholder="Search module, topic, code, department..." onChange={(event) => setSearch(event.target.value)} />
            </label>
            <div className="sort-buttons" aria-label="Sort lessons">
              <button className={sort === "date" ? "active" : ""} type="button" onClick={() => setSort("date")}>Date</button>
              <button className={sort === "department" ? "active" : ""} type="button" onClick={() => setSort("department")}>Dept</button>
              <button className={sort === "topic" ? "active" : ""} type="button" onClick={() => setSort("topic")}>Topic</button>
            </div>
          </section>

          {uploadSummary && (
            <section className="import-summary">
              <strong>{uploadSummary.importedCount} upcoming lessons imported</strong>
              <span>{uploadSummary.ignoredPastCount} past lessons available for insights</span>
              <span>{uploadSummary.totalCount} lessons in file</span>
              {uploadSummary.missingDateCount > 0 && <span>{uploadSummary.missingDateCount} events skipped because they had invalid dates</span>}
            </section>
          )}

          {nextLesson && (
            <section className="next-lesson">
              <div>
                <p className="eyebrow">Next lesson</p>
                <h2>{nextLesson.summary}</h2>
                <p>
                  {formatDay(nextLesson.start)} · {formatTimeRange(nextLesson)} · {nextLesson.moduleCode}
                </p>
              </div>
              <span style={{ background: nextLesson.department?.color || "#8b8d94" }}>{nextLesson.department?.name || "Unrecognized"}</span>
            </section>
          )}

          {events.length > 0 && (
            <section className="insights-board">
              <div className="insight-panel analytics-controls">
                <div className="panel-head">
                  <h2>Insight range</h2>
                  <span className="muted">{analyticsEvents.length} lessons</span>
                </div>
                <div className="range-controls">
                  <div className="sort-buttons">
                    <button className={analyticsScope === "past" ? "active" : ""} type="button" onClick={() => setAnalyticsScope("past")}>Past</button>
                    <button className={analyticsScope === "future" ? "active" : ""} type="button" onClick={() => setAnalyticsScope("future")}>Future</button>
                    <button className={analyticsScope === "both" ? "active" : ""} type="button" onClick={() => setAnalyticsScope("both")}>Both</button>
                  </div>
                  <div className="sort-buttons">
                    <button className={analyticsRange === "3m" ? "active" : ""} type="button" onClick={() => setAnalyticsRange("3m")}>3 mo</button>
                    <button className={analyticsRange === "12m" ? "active" : ""} type="button" onClick={() => setAnalyticsRange("12m")}>Year</button>
                    <button className={analyticsRange === "all" ? "active" : ""} type="button" onClick={() => setAnalyticsRange("all")}>All</button>
                    <button className={analyticsRange === "custom" ? "active" : ""} type="button" onClick={() => setAnalyticsRange("custom")}>Pick</button>
                  </div>
                  {analyticsRange === "custom" && (
                    <div className="custom-dates">
                      <input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} />
                      <input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} />
                    </div>
                  )}
                </div>
              </div>
              <div className="insight-panel radial-panel">
                <div className="panel-head">
                  <h2>{groupSubDepartments ? "Department families" : "Department mix"}</h2>
                  <span className="count-pill">{analyticsEvents.length}</span>
                </div>
                <RadialDepartments insights={departmentInsights} />
              </div>
              <div className="insight-panel">
                <div className="panel-head">
                  <h2>Teaching rhythm</h2>
                  <span className="muted">{formatShortDate(analyticsWindow.start)} - {formatShortDate(analyticsWindow.end)}</span>
                </div>
                <MonthBars months={monthlyInsights} />
              </div>
              <div className="insight-panel">
                <div className="panel-head">
                  <h2>Repeated lesson themes</h2>
                  <span className="muted">selected range</span>
                </div>
                <TopicBars topics={topicInsights} />
              </div>
              <div className="insight-panel">
                <div className="panel-head">
                  <h2>Delivery shape</h2>
                  <span className="muted">all lessons</span>
                </div>
                <DeliveryTiles items={deliveryInsights} />
              </div>
            </section>
          )}

          <section className={`timeline ${viewMode}`}>
            {visibleEvents.length ? (
              [...eventsByDay.entries()].map(([day, dayEvents]) => (
                <div className="day-group" key={day}>
                  <div className="day-head">
                    <div>
                      <strong>{formatDay(new Date(day))}</strong>
                      <span>{dayEvents.length} lessons</span>
                    </div>
                  </div>
                  <div className="lesson-list">
                    {dayEvents.map((event) => (
                      <LessonCard
                        key={event.id}
                        event={event}
                        expanded={expandedEventId === event.id}
                        compact={viewMode === "compact"}
                        onToggle={() => setExpandedEventId(expandedEventId === event.id ? null : event.id)}
                        onAssignCode={() => setDepartmentDraft({ ...emptyDraft, codes: event.moduleCode || event.code })}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState hasUpload={Boolean(uploadSummary)} />
            )}
          </section>
        </section>
      </section>

      {departmentDraft && (
        <DepartmentDialog
          draft={departmentDraft}
          departments={departments}
          onChange={setDepartmentDraft}
          onClose={() => setDepartmentDraft(null)}
          onDelete={departmentDraft.id ? () => deleteDepartment(departmentDraft.id!) : undefined}
          onSave={saveDepartment}
        />
      )}
    </main>
  );
}

function DepartmentFilter({
  active,
  color,
  count,
  detail,
  label,
  onClick,
  onEdit
}: {
  active: boolean;
  color: string;
  count: number;
  detail?: string;
  label: string;
  onClick: () => void;
  onEdit?: () => void;
}) {
  return (
    <div className={`department-filter ${active ? "active" : ""}`}>
      <button type="button" onClick={onClick}>
        <span className="swatch" style={{ background: color }} />
        <span>
          <strong>{label}</strong>
          <small>{detail || `${count} lessons`}</small>
        </span>
      </button>
      {onEdit ? (
        <button className="icon-button" type="button" aria-label={`Edit ${label}`} onClick={onEdit}>
          <Edit3 size={16} />
        </button>
      ) : (
        <span className="count-pill">{count}</span>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="stat-card">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function LessonCard({
  compact,
  event,
  expanded,
  onAssignCode,
  onToggle
}: {
  compact: boolean;
  event: LessonEvent;
  expanded: boolean;
  onAssignCode: () => void;
  onToggle: () => void;
}) {
  const department = event.department || { name: "Unrecognized", color: "#8b8d94" };
  return (
    <article className={`lesson-card ${compact ? "compact" : ""}`}>
      <span className="lesson-accent" style={{ background: department.color }} />
      <button className="lesson-main" type="button" onClick={onToggle}>
        <span className="lesson-time">
          <Clock size={17} />
          {formatTimeRange(event)}
        </span>
        <h3>{event.topic || event.summary}</h3>
        <div className="lesson-tags">
          <span style={{ background: tintColor(department.color), color: department.color }}>{department.name}</span>
          {event.moduleCode && <span>{event.moduleCode}</span>}
          {event.delivery !== "unknown" && <span>{formatDelivery(event.delivery)}</span>}
        </div>
      </button>
      {!event.department && event.moduleCode && (
        <button className="map-button" type="button" onClick={onAssignCode}>
          <Plus size={16} />
          Map code
        </button>
      )}
      {expanded && !compact && (
        <div className="lesson-details">
          <p>{event.summary}</p>
          <dl>
            <div>
              <dt>Module code</dt>
              <dd>{event.moduleCode || "None"}</dd>
            </div>
            <div>
              <dt>Department</dt>
              <dd>{event.department?.name || "Unrecognized"}</dd>
            </div>
            <div>
              <dt>Delivery</dt>
              <dd>{formatDelivery(event.delivery)}</dd>
            </div>
          </dl>
        </div>
      )}
    </article>
  );
}

function DepartmentDialog({
  departments,
  draft,
  onChange,
  onClose,
  onDelete,
  onSave
}: {
  departments: Department[];
  draft: DepartmentDraft;
  onChange: (draft: DepartmentDraft) => void;
  onClose: () => void;
  onDelete?: () => void;
  onSave: () => void;
}) {
  const codes = splitCodes(draft.codes);
  const duplicateCodes = codes.filter((code) =>
    departments.some((department) => department.id !== draft.id && department.codes.map((item) => item.toUpperCase()).includes(code))
  );
  const valid = draft.name.trim().length > 0 && codes.length > 0;

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="department-dialog" role="dialog" aria-modal="true" aria-labelledby="department-dialog-title">
        <div className="dialog-head">
          <div>
            <p className="eyebrow">Department mapping</p>
            <h2 id="department-dialog-title">{draft.id ? "Edit department" : "Add department"}</h2>
          </div>
          <button className="icon-button" type="button" aria-label="Close dialog" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <label>
          Department name
          <input value={draft.name} autoFocus onChange={(event) => onChange({ ...draft, name: event.target.value })} placeholder="e.g. UX Design" />
        </label>

        <label>
          Code or prefixes
          <input value={draft.codes} onChange={(event) => onChange({ ...draft, codes: event.target.value })} placeholder="e.g. UX900, UX" />
        </label>

        <label>
          Parent department
          <select value={draft.parentId || ""} onChange={(event) => onChange({ ...draft, parentId: event.target.value })}>
            <option value="">None - main department</option>
            {departments
              .filter((department) => department.id !== draft.id && !department.parentId)
              .map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
          </select>
        </label>

        {duplicateCodes.length > 0 && <p className="warning-note">Already used: {duplicateCodes.join(", ")}</p>}

        <label>
          Color
          <input value={draft.color} type="color" onChange={(event) => onChange({ ...draft, color: event.target.value })} />
        </label>

        <div className="color-grid">
          {colorOptions.map((color) => (
            <button
              key={color}
              className={draft.color.toLowerCase() === color.toLowerCase() ? "selected" : ""}
              type="button"
              aria-label={`Use color ${color}`}
              style={{ background: color }}
              onClick={() => onChange({ ...draft, color })}
            />
          ))}
        </div>

        <div className="dialog-preview">
          <span className="swatch" style={{ background: draft.color }} />
          <strong>{draft.name || "Department name"}</strong>
          <small>{draft.parentId ? `${getDepartmentById(departments, draft.parentId)?.name || "Parent"} · ` : ""}{codes.join(", ") || "Code or prefixes"}</small>
        </div>

        <div className="dialog-actions">
          {onDelete && (
            <button className="danger-button" type="button" onClick={onDelete}>
              <Trash2 size={18} />
              Delete
            </button>
          )}
          <div />
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="button" disabled={!valid} onClick={onSave}>
            <Save size={18} />
            Save
          </button>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ hasUpload }: { hasUpload: boolean }) {
  return (
    <section className="empty-state">
      <CalendarDays size={42} />
      <h2>{hasUpload ? "No lessons match the current filters" : "Upload an iCal file to begin"}</h2>
      <p>{hasUpload ? "Clear the search or choose a different department." : "The dashboard will show upcoming lessons, recognized departments, and mapping actions for unknown codes."}</p>
    </section>
  );
}

function RadialDepartments({ insights }: { insights: InsightItem[] }) {
  const total = insights.reduce((sum, item) => sum + item.count, 0);
  if (!total) return <p className="muted">No past-year lessons to visualize yet.</p>;

  let offset = 0;
  const gradient = insights
    .map((item) => {
      const start = offset;
      const end = offset + (item.count / total) * 100;
      offset = end;
      return `${item.color} ${start}% ${end}%`;
    })
    .join(", ");

  return (
    <div className="radial-wrap">
      <div className="radial-chart" style={{ background: `conic-gradient(${gradient})` }}>
        <span>{total}</span>
        <small>lessons</small>
      </div>
      <div className="insight-list">
        {insights.slice(0, 5).map((item) => (
          <div key={item.label} className="insight-row">
            <span className="swatch" style={{ background: item.color }} />
            <strong>{item.label}</strong>
            <small>{item.count}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthBars({ months }: { months: MonthInsight[] }) {
  const max = Math.max(1, ...months.map((month) => month.count));
  return (
    <div className="month-bars">
      {months.map((month) => (
        <div key={month.label} className="month-bar">
          <span style={{ height: `${Math.max(8, (month.count / max) * 100)}%` }} />
          <small>{month.label}</small>
          <strong>{month.count}</strong>
        </div>
      ))}
    </div>
  );
}

function TopicBars({ topics }: { topics: InsightItem[] }) {
  const max = Math.max(1, ...topics.map((topic) => topic.count));
  if (!topics.length) return <p className="muted">No topics in the past-year window.</p>;

  return (
    <div className="topic-bars">
      {topics.map((topic) => (
        <div key={topic.label} className="topic-row">
          <div>
            <strong>{topic.label}</strong>
            <small>{topic.count} lessons</small>
          </div>
          <span>
            <i style={{ width: `${(topic.count / max) * 100}%`, background: topic.color }} />
          </span>
        </div>
      ))}
    </div>
  );
}

function DeliveryTiles({ items }: { items: InsightItem[] }) {
  return (
    <div className="delivery-tiles">
      {items.map((item) => (
        <div key={item.label}>
          <span className="swatch" style={{ background: item.color }} />
          <strong>{item.count}</strong>
          <small>{item.label}</small>
        </div>
      ))}
    </div>
  );
}

function groupByDay(events: LessonEvent[]) {
  return events.reduce((days, event) => {
    const day = new Date(event.start.getFullYear(), event.start.getMonth(), event.start.getDate()).toISOString();
    const dayEvents = days.get(day) || [];
    days.set(day, [...dayEvents, event]);
    return days;
  }, new Map<string, LessonEvent[]>());
}

type InsightItem = {
  label: string;
  count: number;
  color: string;
};

type MonthInsight = {
  label: string;
  count: number;
};

function buildDepartmentInsights(events: LessonEvent[], departments: Department[], grouped: boolean): InsightItem[] {
  const counts = new Map<string, InsightItem>();
  events.forEach((event) => {
    const department = getDisplayDepartment(event, departments, grouped);
    const label = department?.name || "Unrecognized";
    const color = department?.color || "#8b8d94";
    const current = counts.get(label) || { label, count: 0, color };
    counts.set(label, { ...current, count: current.count + 1 });
  });
  return [...counts.values()].sort((a, b) => b.count - a.count);
}

function buildTopicInsights(events: LessonEvent[]): InsightItem[] {
  const colors = ["#1f6f78", "#7c3aed", "#dc2626", "#ec4899", "#f97316"];
  const counts = new Map<string, number>();
  events.forEach((event) => {
    const topic = event.topic || "Untitled lesson";
    counts.set(topic, (counts.get(topic) || 0) + 1);
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([label, count], index) => ({ label, count, color: colors[index % colors.length] }));
}

function buildDeliveryInsights(events: LessonEvent[]): InsightItem[] {
  const labels: Record<LessonEvent["delivery"], string> = {
    online: "Online",
    hybrid: "Hybrid",
    onCampus: "On campus",
    unknown: "Unspecified"
  };
  const colors: Record<LessonEvent["delivery"], string> = {
    online: "#38bdf8",
    hybrid: "#14b8a6",
    onCampus: "#1f6f78",
    unknown: "#8b8d94"
  };
  const counts = new Map<LessonEvent["delivery"], number>();
  events.forEach((event) => counts.set(event.delivery, (counts.get(event.delivery) || 0) + 1));
  return (["onCampus", "hybrid", "online", "unknown"] as LessonEvent["delivery"][])
    .map((key) => ({ label: labels[key], count: counts.get(key) || 0, color: colors[key] }))
    .filter((item) => item.count > 0);
}

function buildMonthlyInsights(events: LessonEvent[], startDate: Date, endDate: Date): MonthInsight[] {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const rawCount = monthDiff(start, end) + 1;
  if (rawCount > 36) {
    const years = Array.from({ length: end.getFullYear() - start.getFullYear() + 1 }, (_, index) => ({
      year: start.getFullYear() + index,
      label: String(start.getFullYear() + index),
      count: 0
    }));
    events.forEach((event) => {
      const match = years.find((year) => year.year === event.start.getFullYear());
      if (match) match.count += 1;
    });
    return years.map(({ label, count }) => ({ label, count }));
  }

  const count = Math.max(1, rawCount);
  const months = Array.from({ length: count }, (_, index) => {
    const date = new Date(start.getFullYear(), start.getMonth() + index, 1);
    return {
      date,
      label: new Intl.DateTimeFormat(undefined, { month: "short", year: count > 12 ? "2-digit" : undefined }).format(date),
      count: 0
    };
  });

  events.forEach((event) => {
    const match = months.find((month) => month.date.getFullYear() === event.start.getFullYear() && month.date.getMonth() === event.start.getMonth());
    if (match) match.count += 1;
  });

  return months.map(({ label, count }) => ({ label, count }));
}

function getDisplayDepartment(event: LessonEvent, departments: Department[], grouped: boolean) {
  if (!event.department) return null;
  if (!grouped || !event.department.parentId) return event.department;
  return getDepartmentById(departments, event.department.parentId) || event.department;
}

function getDepartmentById(departments: Department[], id?: string) {
  return departments.find((department) => department.id === id) || null;
}

function getAnalyticsWindow(events: LessonEvent[], today: Date, range: AnalyticsRange, scope: AnalyticsScope, customStart: string, customEnd: string) {
  if (range === "custom" && customStart && customEnd) {
    return { start: parseDateInput(customStart), end: endOfDay(parseDateInput(customEnd)) };
  }

  if (range === "all") {
    const times = events.map((event) => event.start.getTime());
    return {
      start: new Date(Math.min(...times, today.getTime())),
      end: new Date(Math.max(...times, today.getTime()))
    };
  }

  const months = range === "3m" ? 3 : 12;
  if (scope === "future") {
    return { start: today, end: addMonths(today, months) };
  }
  if (scope === "both") {
    return { start: addMonths(today, -months), end: addMonths(today, months) };
  }
  return { start: addMonths(today, -months), end: today };
}

function parseDateInput(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function endOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function addMonths(date: Date, months: number) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

function monthDiff(start: Date, end: Date) {
  return (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
}

function formatShortDate(date: Date) {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "2-digit" }).format(date);
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function formatDelivery(value: LessonEvent["delivery"]) {
  if (value === "onCampus") return "On campus";
  if (value === "online") return "Online";
  if (value === "hybrid") return "Hybrid";
  return "Unspecified";
}

function formatDay(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(date);
}

function formatTimeRange(event: LessonEvent) {
  const start = formatTime(event.start);
  return event.end ? `${start} - ${formatTime(event.end)}` : start;
}

function formatTime(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}
